import React from 'react';
import styled from 'styled-components';
import { useCombobox } from 'downshift';

import { InputGroup, Dropdown, DropdownList, DropdownItem, DropdownSelectArrow } from '../..';

import { measureElement } from '../Popup/measure';

type Matcher = (inputValue?: string, options?: string[]) => string[];

type Props = {
    options: string[];
    label?: string;
    error?: string;
    caption?: string;
    disabled?: boolean;
    value?: string;
    placeholder?: string;
    matchFunction?: Matcher;
    dummyDropdownItemValue?: string;
    defaultFocus?: boolean;
    portalRoot?: string;
    OptionComponent?: React.ComponentType<{ value: string; currentInput: string }>;
    onChange: (selectedOption: string) => void;
    onKeyUp?: (inputValue: string) => void;
    dataLookup?: string;
};

export type ForwardedAutocompleteHandle = {
    clearSelectedItem: () => void;
};

const AutocompleteWrap = styled.div`
    position: relative;
`;

const DropdownWrap = styled.div`
    position: relative;
    z-index: 1;

    & > div {
        width: 100%;
    }
`;

const defaultMatcher: Matcher = (inputValue, options = []) =>
    options.filter(item => (inputValue ? item.toLowerCase().startsWith(inputValue.toLowerCase()) : true));

export const Autocomplete = React.forwardRef<ForwardedAutocompleteHandle, Props>(
    (
        {
            label,
            error,
            caption,
            disabled,
            placeholder,
            value = '',
            options,
            defaultFocus,
            portalRoot,
            matchFunction = defaultMatcher,
            dummyDropdownItemValue,
            OptionComponent,
            onChange,
            onKeyUp = () => {},
            dataLookup,
        }: Props,
        ref,
    ) => {
        const mountedRef = React.useRef(true);
        const inputRef = React.useRef<HTMLInputElement>(null);
        const [itemHeight, setItemHeight] = React.useState(0);
        const [inputOptions, setInputOptions] = React.useState(options);
        const {
            isOpen,
            getToggleButtonProps,
            getMenuProps,
            getInputProps,
            getComboboxProps,
            getItemProps,
            reset,
        } = useCombobox({
            items: inputOptions,
            initialInputValue: value,
            onSelectedItemChange: ({ selectedItem }) => onChange(selectedItem || ''),
            onInputValueChange: ({ inputValue }) => {
                onKeyUp(inputValue || '');
                setInputOptions(matchFunction(inputValue, options));
            },
        });

        const inputProps = getInputProps({
            ref: inputRef,
        });
        const menuProps = getMenuProps(undefined, { suppressRefError: true });
        const toggleButtonProps = disabled ? {} : getToggleButtonProps();

        React.useEffect(() => {
            if (defaultFocus) {
                inputRef.current?.focus();
            }
        }, [defaultFocus]);

        React.useImperativeHandle(ref, () => ({
            clearSelectedItem: () => reset(),
        }));

        React.useEffect(() => {
            // to be able to use a virtualised dropdown scroll container, need to measure a dummy item's height
            //  to calculate sizes inside the dropdown list
            const dropdownValue: string = dummyDropdownItemValue || 'abc';
            const optionElement = (
                <DropdownItem>
                    {OptionComponent ? (
                        <OptionComponent value={dropdownValue} currentInput={dropdownValue} />
                    ) : (
                        dropdownValue
                    )}
                </DropdownItem>
            );
            measureElement(optionElement, ({ height }) => {
                if (mountedRef.current) {
                    setItemHeight(height);
                }
            });

            return () => {
                mountedRef.current = false;
            };
        }, []);

        return (
            <div>
                <AutocompleteWrap {...getComboboxProps()}>
                    <InputGroup
                        {...inputProps}
                        label={label}
                        placeholder={placeholder}
                        error={error}
                        caption={caption}
                        disabled={disabled}
                        aria-label="autocomplete"
                        data-lookup={dataLookup}
                    >
                        <DropdownSelectArrow isOpen={isOpen} disabled={disabled} {...toggleButtonProps} />
                    </InputGroup>
                </AutocompleteWrap>
                <DropdownWrap>
                    <Dropdown
                        isOpen={isOpen && !!inputRef.current}
                        portalRoot={portalRoot}
                        parentRef={inputRef}
                        fullWidth
                    >
                        <DropdownList
                            {...menuProps}
                            getItemHeight={() => itemHeight}
                            height={Math.min(inputOptions.length, 6) * itemHeight}
                        >
                            {inputOptions.map((item, index) => (
                                <DropdownItem key={index} {...getItemProps({ item, index })}>
                                    {!OptionComponent ? (
                                        item
                                    ) : (
                                        <OptionComponent value={item} currentInput={inputProps.value} />
                                    )}
                                </DropdownItem>
                            ))}
                        </DropdownList>
                    </Dropdown>
                </DropdownWrap>
            </div>
        );
    },
);
